import React, { createContext, useContext, useState, ReactNode } from 'react';
import { AlertTriangle, AlertCircle, Info, CheckCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface ConfirmationOptions {
  title: string;
  description?: string;
  variant?: 'default' | 'destructive' | 'warning' | 'info' | 'success';
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

interface ConfirmationContextType {
  confirm: (options: ConfirmationOptions) => void;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

const variantStyles = {
  default: {
    container: "border-glass-border bg-glass-surface",
    icon: "text-primary-600",
    title: "text-foreground",
    description: "text-muted-foreground",
    confirmButton: "glass-button-primary",
    cancelButton: "glass-button-secondary"
  },
  destructive: {
    container: "border-error-500/20 bg-error-500/5",
    icon: "text-error-600",
    title: "text-error-800",
    description: "text-error-600",
    confirmButton: "bg-error-600 hover:bg-error-700 text-white border-error-600",
    cancelButton: "glass-button-secondary"
  },
  warning: {
    container: "border-warning-500/20 bg-warning-500/5",
    icon: "text-warning-600",
    title: "text-warning-800",
    description: "text-warning-600",
    confirmButton: "bg-warning-600 hover:bg-warning-700 text-white border-warning-600",
    cancelButton: "glass-button-secondary"
  },
  info: {
    container: "border-primary-500/20 bg-primary-500/5",
    icon: "text-primary-600",
    title: "text-primary-800",
    description: "text-primary-600",
    confirmButton: "glass-button-primary",
    cancelButton: "glass-button-secondary"
  },
  success: {
    container: "border-success-500/20 bg-success-500/5",
    icon: "text-success-600",
    title: "text-success-800",
    description: "text-success-600",
    confirmButton: "bg-success-600 hover:bg-success-700 text-white border-success-600",
    cancelButton: "glass-button-secondary"
  }
};

const getIcon = (variant: ConfirmationOptions['variant'] = 'default') => {
  switch (variant) {
    case 'destructive':
      return <AlertCircle className="h-6 w-6" />;
    case 'warning':
      return <AlertTriangle className="h-6 w-6" />;
    case 'info':
      return <Info className="h-6 w-6" />;
    case 'success':
      return <CheckCircle className="h-6 w-6" />;
    default:
      return <AlertTriangle className="h-6 w-6" />;
  }
};

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  options: ConfirmationOptions | null;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({ isOpen, onClose, options }) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen || !options) return null;

  const variant = options.variant || 'default';
  const styles = variantStyles[variant];

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await options.onConfirm();
      onClose();
    } catch (error) {
      console.error('Confirmation action failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (options.onCancel) {
      options.onCancel();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={handleCancel}
      />
      
      {/* Dialog */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className={cn(
          "glass-card rounded-2xl border p-6 shadow-2xl",
          "animate-in zoom-in-95 duration-200",
          styles.container
        )}>
          <div className="flex items-start gap-4">
            <div className={cn("flex-shrink-0", styles.icon)}>
              {getIcon(variant)}
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className={cn("text-lg font-semibold mb-2", styles.title)}>
                {options.title}
              </h3>
              
              {options.description && (
                <p className={cn("text-sm mb-6", styles.description)}>
                  {options.description}
                </p>
              )}
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCancel}
                  disabled={isLoading}
                  className={cn(
                    "px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200",
                    "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    styles.cancelButton
                  )}
                >
                  {options.cancelText || 'Cancel'}
                </button>
                
                <button
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className={cn(
                    "px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200",
                    "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    "flex items-center gap-2",
                    styles.confirmButton
                  )}
                >
                  {isLoading && (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  )}
                  {options.confirmText || 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ConfirmationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationOptions | null>(null);

  const confirm = (newOptions: ConfirmationOptions) => {
    setOptions(newOptions);
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setOptions(null);
  };

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      <ConfirmationDialog isOpen={isOpen} onClose={close} options={options} />
    </ConfirmationContext.Provider>
  );
};

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
};

// Convenience method for common confirmation patterns
export const confirm = {
  delete: (itemName: string, onConfirm: () => void | Promise<void>) => {
    // This will be implemented when the hook is used
    console.log('Delete confirmation:', itemName, onConfirm);
  },
  destructive: (title: string, description: string, onConfirm: () => void | Promise<void>) => {
    console.log('Destructive confirmation:', title, description, onConfirm);
  }
};
